import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IconAlertCircle } from "@/lib/icons";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import {
  type LabTestDefinition,
  createLabTestDefinition,
  updateLabTestDefinition,
} from "@/lib/api/laboratory";

const PANELS = ["cbc", "biochem", "urinalysis", "serology", "hormones", "other"];
const SPECIMENS = ["whole_blood", "serum", "plasma", "urine", "stool", "swab"];

interface Props {
  opened: boolean;
  onClose: () => void;
  initial?: LabTestDefinition | null;
}

export function LabTestFormDialog({ opened, onClose, initial }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!initial;

  const form = useForm({
    initialValues: {
      name_uz: initial?.name_uz ?? initial?.name_ru ?? "",
      unit: initial?.unit ?? "",
      panel: initial?.panel ?? "",
      specimen: initial?.specimen ?? "",
      normal_low_male: initial?.normal_low_male ?? null,
      normal_high_male: initial?.normal_high_male ?? null,
      normal_low_female: initial?.normal_low_female ?? null,
      normal_high_female: initial?.normal_high_female ?? null,
    },
    validate: {
      name_uz: (v) =>
        v.trim().length > 0 ? null : t("validation.required"),
    },
  });

  useEffect(() => {
    if (opened) {
      const num = (v: unknown): number | null =>
        v === null || v === undefined || v === "" ? null : Number(v);
      form.setValues({
        name_uz: initial?.name_uz ?? initial?.name_ru ?? "",
        unit: initial?.unit ?? "",
        panel: initial?.panel ?? "",
        specimen: initial?.specimen ?? "",
        normal_low_male: num(initial?.normal_low_male),
        normal_high_male: num(initial?.normal_high_male),
        normal_low_female: num(initial?.normal_low_female),
        normal_high_female: num(initial?.normal_high_female),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initial?.code]);

  const slugify = (name: string): string => {
    const map: Record<string, string> = {
      "'": "", "ʼ": "", "ʻ": "",
      "ў": "u", "ғ": "g", "қ": "q", "ҳ": "h",
      "ä": "a", "ö": "o",
    };
    return name
      .trim()
      .toLowerCase()
      .split("")
      .map((c) => map[c] ?? c)
      .join("")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40) || `test_${Date.now()}`;
  };

  const mutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      const uz = values.name_uz.trim();
      const body = {
        // backend requires name_ru — store the same Uzbek name there
        name_ru: uz,
        name_uz: uz,
        name_en: null,
        unit: values.unit.trim() || null,
        panel: values.panel.trim() || null,
        specimen: values.specimen.trim() || null,
        normal_low_male:
          typeof values.normal_low_male === "number" ? values.normal_low_male : null,
        normal_high_male:
          typeof values.normal_high_male === "number" ? values.normal_high_male : null,
        normal_low_female:
          typeof values.normal_low_female === "number" ? values.normal_low_female : null,
        normal_high_female:
          typeof values.normal_high_female === "number" ? values.normal_high_female : null,
      };
      if (isEdit) {
        return updateLabTestDefinition(initial!.code, body);
      }
      return createLabTestDefinition({
        code: slugify(uz),
        ...body,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-test-definitions"] });
      notifications.show({
        color: "green",
        message: isEdit ? t("references.lab_updated") : t("references.lab_created"),
      });
      form.reset();
      onClose();
    },
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) {
      if (mutation.error.code === "conflict") return t("references.lab_duplicate");
      return mutation.error.message;
    }
    return t("auth.network_error");
  })();

  return (
    <Modal
      opened={opened}
      onClose={() => {
        form.reset();
        onClose();
      }}
      title={isEdit ? t("references.lab_edit") : t("references.lab_create")}
      size="lg"
      radius="lg"
    >
      {errorMessage && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="md" radius="lg">
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={form.onSubmit((v) => mutation.mutate(v))}>
        <Stack>
          <TextInput
            label={t("references.lab_fields.name_uz")}
            placeholder="Gemoglobin"
            required
            {...form.getInputProps("name_uz")}
          />
          <Select
            label={t("references.lab_fields.panel")}
            placeholder={t("references.lab_panels.cbc")}
            clearable
            data={PANELS.map((p) => ({
              value: p,
              label: t(`references.lab_panels.${p}`, { defaultValue: p }),
            }))}
            {...form.getInputProps("panel")}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t("references.lab_test_fields.unit")}
              placeholder="g/L"
              {...form.getInputProps("unit")}
            />
            <Select
              label={t("references.lab_fields.specimen")}
              placeholder={t("references.lab_specimens.whole_blood")}
              clearable
              data={SPECIMENS.map((s) => ({
                value: s,
                label: t(`references.lab_specimens.${s}`, { defaultValue: s }),
              }))}
              {...form.getInputProps("specimen")}
            />
          </SimpleGrid>
          <Stack gap="xs">
            <Text size="xs" fw={700} tt="uppercase" c="brand.7" mt="xs">
              {t("references.lab_test_fields.male_range")}
            </Text>
            <SimpleGrid cols={2} spacing="md">
              <NumberInput
                label={t("references.lab_fields.range_low")}
                placeholder="130"
                decimalScale={4}
                {...form.getInputProps("normal_low_male")}
              />
              <NumberInput
                label={t("references.lab_fields.range_high")}
                placeholder="180"
                decimalScale={4}
                {...form.getInputProps("normal_high_male")}
              />
            </SimpleGrid>
          </Stack>
          <Stack gap="xs">
            <Text size="xs" fw={700} tt="uppercase" c="brand.7" mt="xs">
              {t("references.lab_test_fields.female_range")}
            </Text>
            <SimpleGrid cols={2} spacing="md">
              <NumberInput
                label={t("references.lab_fields.range_low")}
                placeholder="120"
                decimalScale={4}
                {...form.getInputProps("normal_low_female")}
              />
              <NumberInput
                label={t("references.lab_fields.range_high")}
                placeholder="160"
                decimalScale={4}
                {...form.getInputProps("normal_high_female")}
              />
            </SimpleGrid>
          </Stack>
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={mutation.isPending} radius="xl">
              {isEdit ? t("common.save") : t("common.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
